import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PlansHeader } from "./PlansHeader";
import { PlansFormWrapper } from "./PlansFormWrapper";
import { PlansTableWrapper } from "./PlansTableWrapper";
import { usePlans } from "@/hooks/usePlans";
import type { Plan, PlanFormData } from "@/types/plan";
import { useAuth } from "@/hooks/useAuth";

export const PlansSettings = () => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const { plans, isLoading, addPlan, updatePlan } = usePlans();
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const handleSubmit = async (data: PlanFormData) => {
    try {
      // Validações
      if (!data.name || !data.price || !data.recurrence || !data.enable_agent) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Todos os campos são obrigatórios.",
        });
        return;
      }

      // Verificar se já existe um plano com o mesmo nome
      const existingPlan = plans.find(
        (plan) => plan.name.toLowerCase() === data.name.toLowerCase() && 
        (!editingId || plan.id !== editingId)
      );

      if (existingPlan) {
        toast({
          variant: "destructive",
          title: "Erro",
          description: "Já existe um plano com este nome.",
        });
        return;
      }

      // Adicionar campos automáticos
      const enrichedData = {
        ...data,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (editingId) {
        await updatePlan(editingId, enrichedData);
        toast({
          title: "Sucesso",
          description: "Plano atualizado com sucesso!",
        });
      } else {
        await addPlan(enrichedData);
        toast({
          title: "Sucesso",
          description: "Plano criado com sucesso!",
        });
      }
      handleClear();
    } catch (error: any) {
      console.error('Error saving plan:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro ao salvar o plano.",
      });
    }
  };

  const handleEdit = (plan: Plan) => {
    setEditingId(plan.id);
  };

  const handleClear = () => {
    setEditingId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <PlansHeader />
        <PlansFormWrapper
          onSubmit={handleSubmit}
          selectedPlan={editingId ? plans.find((plan) => plan.id === editingId) : undefined}
          onClear={handleClear}
          isEditing={!!editingId}
        />
      </Card>

      <Card>
        <PlansTableWrapper plans={plans} onEdit={handleEdit} />
      </Card>
    </div>
  );
};